import * as Sentry from '@sentry/react';
import { onAction } from 'mobx-state-tree';
import { SENTRY_DSN, SENTRY_ENV } from './config';
import { Store } from './models';
import { DateTime } from 'luxon';
// @ts-expect-error Typescript cant find the lib
import crypto from 'crypto-browserify';

function setSentryUser(store: Store): void {
    onAction(
        store.account,
        call => {
            if (call.name === 'setAccount') {
                if (!call.args) {
                    Sentry.captureMessage(
                        'AccountController.setAccount called without arguments'
                    );
                    return;
                }
                const account = call.args[0];
                if (account)
                    Sentry.setUser({
                        id: account.id.toString(),
                        email: account.email,
                        name: account.name,
                    });
            }
        },
        true
    );
}

function beforeBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
    if (breadcrumb.category === 'ui.click')
        breadcrumb.message = breadcrumb.message
            ? window.document.querySelector(breadcrumb.message)?.textContent +
              ' ' +
              breadcrumb.message
            : breadcrumb.message;

    return breadcrumb;
}

type ErrorCount = {
    lastSecond: number;
    lastMinute: number;
    countSecond: number;
    countMinute: number;
};

const sentryErrorMap = new Map<string, ErrorCount>();
const MAX_ERRORS_PER_SECOND = 3;
const MAX_ERRORS_PER_MINUTE = 10;

// eslint-disable-next-line complexity
function beforeSend(
    event: Sentry.Event,
    hint?: Sentry.EventHint
): Sentry.Event | null {
    if (!hint?.originalException) return event;

    const hash = crypto
        .createHash('md5')
        .update(JSON.stringify(hint.originalException))
        .digest('hex');

    const currentTime = DateTime.now().toSeconds();
    const currentSecond = Math.trunc(currentTime);
    const currentMinute = Math.trunc(currentTime / 60);
    const inMap = sentryErrorMap.get(hash);
    const countSecond = inMap?.countSecond ?? -1;
    const countMinute = inMap?.countMinute ?? -1;
    const repeatInSameSecond = (inMap?.lastSecond ?? 0) === currentSecond;
    const repeatInSameMinute = (inMap?.lastMinute ?? 0) === currentMinute;

    sentryErrorMap.set(hash, {
        lastSecond: currentSecond,
        lastMinute: currentMinute,
        countSecond: repeatInSameSecond ? countSecond + 1 : 0,
        countMinute: repeatInSameMinute ? countMinute + 1 : 0,
    });

    if (countSecond + 1 >= MAX_ERRORS_PER_SECOND && repeatInSameSecond)
        return null;

    if (countMinute + 1 >= MAX_ERRORS_PER_MINUTE && repeatInSameMinute)
        return null;

    return event;
}

function sentryInit(store: Store): void {
    const exceptionsToFilterOut = [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications.',
        'Feature access is restricted or disabled',
        'Invalid recaptcha token or action',
        'Insufficient points',
        'reCAPTCHA has already been rendered in this element',
        'Low score recaptcha token',
        'Invalid token',
        "Cannot read properties of null (reading 'style')",
        'Network Error',
        'Request aborted',
    ];

    if (process.env.NODE_ENV === 'production') setSentryUser(store);
    Sentry.init({
        dsn: SENTRY_DSN,
        environment: SENTRY_ENV,
        beforeBreadcrumb,
        beforeSend,
        ignoreErrors: exceptionsToFilterOut,
    });
}

function sentryErrorWrapper(fn: (...param: unknown[]) => unknown) {
    return (...param: unknown[]): void => {
        try {
            fn(...param);
        } catch (err) {
            Sentry.captureException(err);
        }
    };
}

function sentryCaptureMessage(message: string): void {
    Sentry.captureMessage(message);
}

export { sentryErrorWrapper, sentryInit, sentryCaptureMessage };
